import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Plus, Trash2, Database, Plane } from "lucide-react";
import { toast } from "sonner";

const SOURCE_DESCRIPTIONS = {
  GDS: "Global Distribution System — traditional published fares (most carriers).",
  NDC: "New Distribution Capability — direct airline content with richer ancillaries.",
  LTC: "Lite Ticketing Content — low-cost carriers & lite-cost feed.",
  PYTON: "Python aggregator — aggregated long-tail carriers.",
  EAC: "Enterprise Aggregator Connections — premium consolidated content.",
};

export default function AdminSuppliers() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ code: "", name: "", type: "flight" });
  const [amadeusOpen, setAmadeusOpen] = useState(true);
  const [sources, setSources] = useState({});
  const [sourcesSaving, setSourcesSaving] = useState(false);
  const [airlines, setAirlines] = useState([]);
  const [airlineForm, setAirlineForm] = useState({ code: "", name: "" });

  const load = async () => {
    const { data } = await api.get("/admin/suppliers");
    setRows(data);
    try {
      const { data: s } = await api.get("/admin/suppliers/amadeus/sources");
      setSources(s?.sources || {});
    } catch (_e) { /* ignore */ }
    try {
      const { data: a } = await api.get("/admin/airlines");
      setAirlines(a || []);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggleSupplier = async (id) => { await api.post(`/admin/suppliers/${id}/toggle`); load(); };
  const addSupplier = async () => {
    if (!f.code || !f.name) return toast.error("Code & name required");
    await api.post("/admin/suppliers", f);
    toast.success("Supplier added");
    setF({ code: "", name: "", type: "flight" });
    load();
  };

  // ---------- Amadeus content sources ----------
  const toggleSource = async (key, val) => {
    const next = { ...sources, [key]: !!val };
    setSources(next);
    setSourcesSaving(true);
    try {
      await api.put("/admin/suppliers/amadeus/sources", { sources: next });
      toast.success(`${key} ${val ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Failed to update source");
      setSources(sources); // rollback
    } finally { setSourcesSaving(false); }
  };

  // ---------- Per-airline activation ----------
  const setAirline = async (code, name, active) => {
    try {
      await api.put(`/admin/airlines/${code.toUpperCase()}`, { code: code.toUpperCase(), name, active });
      toast.success(`${code.toUpperCase()} ${active ? "activated" : "deactivated"}`);
      load();
    } catch (_e) { toast.error("Failed to update airline"); }
  };
  const clearAirlineOverride = async (code) => {
    try {
      await api.delete(`/admin/airlines/${code.toUpperCase()}`);
      toast.success(`${code.toUpperCase()} reset to default (active)`);
      load();
    } catch (_e) { toast.error("Failed"); }
  };
  const addAirlineOverride = async () => {
    if (!airlineForm.code || airlineForm.code.length < 2) return toast.error("IATA code is required (e.g. TP, EK)");
    await setAirline(airlineForm.code, airlineForm.name || airlineForm.code.toUpperCase(), false);
    setAirlineForm({ code: "", name: "" });
  };

  const amadeusRow = rows.find((r) => r.code === "amadeus");

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Suppliers / Airlines</h1>
      <p className="text-slate-500 mt-1">Activate or deactivate any supplier, content source or marketing airline for the B2C funnel.</p>

      <Card className="mt-6 p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input data-testid="sup-code" placeholder="Code (e.g. AI, amadeus)" value={f.code} onChange={e => setF({ ...f, code: e.target.value })} />
          <Input data-testid="sup-name" placeholder="Display name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
          <Select value={f.type} onValueChange={v => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="sup-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["flight","hotel","car","package","sightseeing"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="sup-add" onClick={addSupplier} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">Add supplier</Button>
        </div>
      </Card>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`sup-row-${r.id}`}>
                <TableCell className="font-mono">{r.code}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><Badge className="bg-teal-50 text-teal-700 border-0 capitalize">{r.type}</Badge></TableCell>
                <TableCell><Badge className={(r.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700") + " border-0"}>{r.active ? "active" : "inactive"}</Badge></TableCell>
                <TableCell><Switch data-testid={`sup-toggle-${r.id}`} checked={r.active} onCheckedChange={() => toggleSupplier(r.id)} /></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No suppliers</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* ---------- Amadeus content sources sub-panel ---------- */}
      {amadeusRow && (
        <Card className="mt-5 p-5" data-testid="amadeus-sources-card">
          <button
            type="button"
            onClick={() => setAmadeusOpen(!amadeusOpen)}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-teal-100 text-[var(--brand-accent)] flex items-center justify-center"><Database className="w-5 h-5" /></div>
              <div>
                <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Amadeus content sources</div>
                <div className="text-xs text-slate-500">Toggle individual feeds Amadeus uses for flight inventory. Disabled feeds are skipped at search.</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>{Object.values(sources).filter(Boolean).length}/{Object.keys(SOURCE_DESCRIPTIONS).length} on</span>
              {amadeusOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </div>
          </button>

          {amadeusOpen && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="amadeus-sources-grid">
              {Object.entries(SOURCE_DESCRIPTIONS).map(([key, desc]) => (
                <div key={key} className={`border rounded-xl p-3 flex items-start justify-between gap-3 ${sources[key] ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40"}`}>
                  <div>
                    <div className="font-mono font-bold text-[var(--brand-accent)] flex items-center gap-2">
                      {key}
                      {sources[key] ? <span className="text-[10px] uppercase tracking-wider text-emerald-700">enabled</span>
                                     : <span className="text-[10px] uppercase tracking-wider text-slate-400">disabled</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 max-w-md">{desc}</div>
                  </div>
                  <Switch
                    data-testid={`amadeus-source-${key}`}
                    checked={!!sources[key]}
                    disabled={sourcesSaving}
                    onCheckedChange={(v) => toggleSource(key, v)}
                  />
                </div>
              ))}
            </div>
          )}
          {amadeusOpen && Object.values(sources).every((v) => !v) && (
            <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              ⚠ All Amadeus sources are disabled — B2C searches will return no results until you re-enable at least one.
            </div>
          )}
        </Card>
      )}

      {/* ---------- Per-airline activation ---------- */}
      <Card className="mt-5 p-5" data-testid="airlines-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-100 to-amber-100 text-[var(--brand-accent)] flex items-center justify-center"><Plane className="w-5 h-5" /></div>
            <div>
              <div className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Marketing airline activation</div>
              <div className="text-xs text-slate-500">Deactivate specific carriers so their flights never reach the B2C funnel (e.g. legal or commercial blocks). Default: all airlines active.</div>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input data-testid="airline-code-input" placeholder="IATA code (e.g. TP, EK)" maxLength={3} value={airlineForm.code} onChange={(e) => setAirlineForm({ ...airlineForm, code: e.target.value.toUpperCase() })} />
          <Input data-testid="airline-name-input" placeholder="Display name (optional)" value={airlineForm.name} onChange={(e) => setAirlineForm({ ...airlineForm, name: e.target.value })} className="md:col-span-2" />
          <Button data-testid="airline-add-deactivate" onClick={addAirlineOverride} variant="outline" className="rounded-full">
            <Plus className="w-3.5 h-3.5 mr-1" /> Deactivate airline
          </Button>
        </div>

        <div className="mt-4 overflow-auto">
          {airlines.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-6">No overrides — every carrier returned by Amadeus is active on B2C.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>IATA</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead className="w-32">Active</TableHead><TableHead className="w-32"></TableHead></TableRow></TableHeader>
              <TableBody>
                {airlines.map((a) => (
                  <TableRow key={a.code} data-testid={`airline-row-${a.code}`}>
                    <TableCell className="font-mono font-bold">{a.code}</TableCell>
                    <TableCell className="text-slate-700">{a.name || "—"}</TableCell>
                    <TableCell>
                      <Badge className={(a.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700") + " border-0"}>{a.active ? "active" : "inactive"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        data-testid={`airline-toggle-${a.code}`}
                        checked={!!a.active}
                        onCheckedChange={(v) => setAirline(a.code, a.name || "", v)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" className="text-slate-500 hover:text-red-600" data-testid={`airline-clear-${a.code}`} onClick={() => clearAirlineOverride(a.code)}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Reset
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
